import { Selector } from '../ui/Selectors'

/**
 * Address model (ID-based DOM reads)
 * - Constructor defaults keep DOM bindings stable (no nulls).
 * - fromAPI() converts DB nulls -> '' for the UI.
 * - readFromForm() pulls current values from the DOM by element IDs.
 * - toAPI() converts '' -> null for saving to the backend.
 */
export class Address {
    constructor() {
        this.street1 = ''
        this.street2 = ''
        this.npa = ''
        this.city = ''
        this.state = ''
        this.country = ''
        this.country_id = '' // raw id from select
        this.iso_3166_2 = '' // saved from <select id="country_id"> option[data-iso]
        this.location_point = '' // keep string for UI; null out on save if empty
    }

    /**
     * Hydrate from API/DB payload (may contain nulls).
     * @param {Object} raw
     */
    static fromAPI(raw = {}) {
        const a = new Address()
        a.street1 = raw.street1 ?? ''
        a.street2 = raw.street2 ?? ''
        a.npa = raw.npa ?? ''
        a.city = raw.city ?? ''
        a.state = raw.state ?? ''

        // country can be a string or an object { code, name }
        if (typeof raw.country === 'object' && raw.country !== null) {
            a.country = raw.country.name ?? ''
            a.iso_3166_2 = raw.iso_3166_2 ?? raw.country.code ?? ''
            a.country_id = raw.country_id ?? raw.country.id ?? ''
        } else {
            a.country = raw.country ?? ''
            a.iso_3166_2 = raw.iso_3166_2 ?? ''
            a.country_id = raw.country_id ?? ''
        }

        a.location_point = raw.location_point ?? ''
        return a
    }

    /**
     * Read current values from the DOM using element IDs.
     * Expects inputs with IDs: address, npa, city, state
     * And a country <select> with id="country_id" (or name="country_id")
     * whose selected <option> carries data-iso="XX".
     * @param {Document|HTMLElement} root
     */
    readFromForm(root = document) {
        const val = (id) => (document.getElementById(id)?.value ?? '').trim()

        this.street1 = val('address') // single input maps to street1
        this.street2 = '' // not present today
        this.npa = val('npa')
        this.city = val('city')
        this.state = val('state')

        const countrySelect = Address.getCountrySelect(root)
        this.country_id = countrySelect?.value?.trim() || ''
        this.iso_3166_2 = Address.getIsoFromCountrySelect(root)
        return this
    }

    /**
     * Build API payload ('' -> null).
     */
    toAPI() {
        const nz = (v) => {
            if (v == null) return null
            const s = String(v).trim()
            return s === '' ? null : s
        }
        return {
            street1: nz(this.street1),
            street2: nz(this.street2),
            npa: nz(this.npa),
            city: nz(this.city),
            state: nz(this.state),
            country: nz(this.country), // include only if your API accepts it
            country_id: nz(this.country_id),
            iso_3166_2: nz(this.iso_3166_2), // primary country code you save
            location_point: nz(this.location_point),
        }
    }

    /**
     * - prefer #country_id, else [name="country_id"]
     * - read data-iso from the selected <option>
     * - return '' if not found
     * @param {Document|HTMLElement} root
     */
    static getIsoFromCountrySelect(root = document) {
        const el = Address.getCountrySelect(root)
        if (!el) return ''

        const selected = (el.selectedOptions && el.selectedOptions[0]) || el.querySelector?.('option:checked') || null

        if (!selected) return ''
        return selected.dataset?.iso || selected.getAttribute('data-iso') || selected.value || ''
    }

    /**
     * Helper: locate the country select element.
     * @param {Document|HTMLElement} root
     * @returns {HTMLSelectElement|null}
     */
    static getCountrySelect(root = document) {
        return root.querySelector(Selector.countryIDInput) || root.querySelector('[name="country_id"]')
    }
}
